import React, { useEffect } from 'react'
import type { GoodsItem } from '../types'
import PaymentQRCode from './PaymentQRCode'
import { usePaymentOrder } from '../hooks/usePaymentOrder'
import {
  getPrimarySecondaryForTab,
  getRenewalHintBeforeInvoiceLine
} from '../utils'
import paymentConfig from '../../../../constants/payment'
import photoStorageIcon from '../images/advance/photo_storage.png'
import photoViewerIcon from '../images/advance/photo_viewer.png'
import photoDownIcon from '../images/advance/photo_down.png'
import peopleMaxIcon from '../images/advance/people_max.png'
import groupWatermarkIcon from '../images/advance/group_watermark.png'
import photoEditWatermarkIcon from '../images/advance/photo_edit_watermark.png'

interface Props {
  goodsList?: GoodsItem[]
  selectedGoods?: GoodsItem | null
  onSelect?: (g: GoodsItem) => void
  onBuy?: () => void
  onClose?: () => void
  clickInvoice?: () => void
  clickAllRights?: () => void
  onConsult?: () => void
  /** 商品卡片上方自定义横幅（开通弹窗 cardBanner 插槽） */
  cardBanner?: React.ReactNode
}

const AdvancedPanel: React.FC<Props> = ({
  goodsList = [],
  selectedGoods,
  onSelect,
  onBuy,
  onClose,
  clickInvoice,
  clickAllRights,
  onConsult,
  cardBanner
}) => {
  const {
    alipayUrl,
    wechatUrl,
    qrCodeInvalid,
    channel,
    setChannel,
    isCreatingOrder,
    createOrder
  } = usePaymentOrder(selectedGoods, {
    sensorClickEvent: () => {
      /* noop */
    },
    onClose:
      onClose ||
      (() => {
        /* noop */
      })
  })

  const { primary, secondary } = getPrimarySecondaryForTab(
    goodsList,
    'advanced'
  )
  const panelGoods = [primary, secondary].filter(Boolean) as GoodsItem[]
  const isPriceDiffItem = (g?: GoodsItem) =>
    !!(
      g &&
      (g.content?.title?.includes('补差价') ||
        g.content?.title === '补差价升级' ||
        g.content?.sign === '最划算')
    )
  const hasPriceDiff = (goodsList || []).some(isPriceDiffItem)
  const renewalHint = getRenewalHintBeforeInvoiceLine(selectedGoods)

  return (
    <div className="panel advanced-panel basic-panel">
      <div className="panel-content">
        <div className="advanced-layout">
          <div className="advanced-left">
            {cardBanner && (
              <div className="panel-card-banner">{cardBanner}</div>
            )}
            <div className="left-top cards">
              {panelGoods.map((item, idx) => (
                <div
                  key={idx}
                  className={`card ${
                    idx === 0 ? 'primary-card' : 'secondary-card'
                  } ${selectedGoods?.id === item?.id ? 'active' : ''}`}
                  onClick={() => item && onSelect && onSelect(item)}
                >
                  {item?.content?.sign &&
                    (!hasPriceDiff || isPriceDiffItem(item)) && (
                      <div className="card-badge">{item.content.sign}</div>
                    )}
                  <div className="card-title">
                    {item?.content?.title ||
                      (idx === 0 ? '连续包年' : '单独包年')}
                  </div>
                  <div className="card-price">
                    <span className="currency">¥</span>
                    <span className="amount">{item?.price ?? '--'}</span>
                  </div>
                  <div className="card-subtitle">{item?.content?.subTitle}</div>
                </div>
              ))}
            </div>
            <div className="apply-invoice">
              {renewalHint ? (
                <span>
                  {renewalHint}
                  {!/[，,]$/.test(renewalHint) ? '，' : ''}
                </span>
              ) : null}
              支付完成后可&nbsp;
              <a
                onClick={clickInvoice}
                style={{ color: '#0070F4', cursor: 'pointer' }}
              >
                申请发票
              </a>
              ｜当前会员仅适用所选团队，多团队请&nbsp;
              <a
                onClick={onConsult}
                style={{ color: '#0070F4', cursor: 'pointer' }}
              >
                联系客服
              </a>
            </div>
            <div className="left-bottom benefits">
              <div className="benefits-header">
                <h4>会员权益</h4>
                <a className="view-all" onClick={clickAllRights}>
                  查看全部权益
                  <svg
                    width="7"
                    height="12"
                    viewBox="0 0 7 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginLeft: 6, verticalAlign: 'middle' }}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.5818e-05 0.883884L5.00006 5.88388L5.61359e-05 10.8839L0.883939 11.7678L6.76782 5.88388L0.883939 1.87028e-07L5.5818e-05 0.883884Z"
                      fill="#0070F4"
                    />
                  </svg>
                </a>
              </div>
              <ul>
                {[
                  {
                    icon: photoStorageIcon,
                    title: '照片存储20万张',
                    subtitle: '超大空间永久存储'
                  },
                  {
                    icon: photoViewerIcon,
                    title: '照片查看2年内',
                    subtitle: '近1年照片随时可查'
                  },
                  {
                    icon: photoDownIcon,
                    title: '照片/台账批量下载',
                    subtitle: '批量导出更省时'
                  },
                  {
                    icon: peopleMaxIcon,
                    title: '人数上限100人',
                    subtitle: '共享协作更顺畅'
                  },
                  {
                    icon: groupWatermarkIcon,
                    title: '团队水印8个',
                    subtitle: '多套模板统一使用'
                  },
                  {
                    icon: photoEditWatermarkIcon,
                    title: '编辑水印/去水印',
                    subtitle: '赠送3000次编辑次数'
                  }
                ].map((b, idx) => (
                  <li key={idx}>
                    <img className="benefit-img" src={b.icon} alt={b.title} />
                    <div className="benefit-text">
                      <div className="benefit-title">{b.title}</div>
                      <div className="benefit-subtitle">{b.subtitle}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="advanced-right">
            <PaymentQRCode
              alipayUrl={alipayUrl}
              wechatUrl={wechatUrl}
              qrCodeInvalid={qrCodeInvalid}
              onRefresh={() => {
                createOrder(true)
              }}
              channel={channel}
              price={selectedGoods?.price}
              setChannel={(c: number) => {
                setChannel(c)
              }}
              alipayOnly={selectedGoods?.type === 2}
              isContinuous={
                selectedGoods?.content?.vipType ===
                  paymentConfig.paymentType.multipleMonth ||
                selectedGoods?.content?.vipType ===
                  paymentConfig.paymentType.multipleYear ||
                selectedGoods?.content?.vipType ===
                  paymentConfig.paymentType.periodQuarter
              }
              isCreatingOrder={isCreatingOrder}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdvancedPanel
